import type { ColorName } from '@emotion/react';
import { type ResourceType, TaskCode } from '@quromedical/fhir-common';
import type { TaskGetResponse } from '@quromedical/models';
import { compact } from '@quromedical/utils';
import type { PrimitiveValueType } from 'components/types';
import type { IconName } from 'design-system';
import { shortMonthDateFormatter } from 'helpers';
import type { useTasksProps } from 'hooks/useTasks';
import type React from 'react';
import { strings } from 'strings';

import type { NavigateToTask } from './types';

const supportedUpdates = [
  'completed',
  'in-progress',
  'requested',
  'cancelled',
] as const;

const supportedFilters = [
  ...supportedUpdates,
  'active',
  'inactive',
  'failed',
] as const;

const supportedOwnerFilters = ['me', 'team', 'all'];

export type SupportedUpdateStatus = (typeof supportedUpdates)[number];

export type SupportedFilterStatus = (typeof supportedFilters)[number];

export type StatusFilter = SupportedFilterStatus | 'all';
export type OwnerFilter = string | 'me' | 'team' | 'all';

export const statusUpdateDisplays: Record<SupportedUpdateStatus, string> = {
  requested: strings.TaskStatusDisplayRequested,
  'in-progress': strings.TaskStatusDisplayInProgress,
  completed: strings.TaskStatusDisplayCompleted,
  cancelled: strings.TaskStatusDisplayCancelled,
};

export const statusFilterDisplays: Record<SupportedFilterStatus, string> = {
  ...statusUpdateDisplays,
  active: strings.TaskStatusDisplayActive,
  inactive: strings.TaskStatusDisplayInactive,
  failed: strings.TaskStatusDisplayOverdue,
};

export const ownerFilterDisplays: Record<OwnerFilter, string> = {
  me: strings.TaskLabelMe,
  team: strings.TaskLabelTeam,
};

export const isSupportedFilter = (
  filter: PrimitiveValueType,
): filter is SupportedFilterStatus =>
  supportedFilters.includes(filter as SupportedFilterStatus);

export const isSupportedOwnerFilter = (
  filter: PrimitiveValueType,
): filter is OwnerFilter =>
  supportedOwnerFilters.includes(filter as OwnerFilter);

export const statusIcons: Partial<Record<SupportedFilterStatus, IconName>> = {
  requested: 'access-time',
  'in-progress': 'event-busy',
  failed: 'error-outline',
  completed: 'check-circle-outline',
  cancelled: 'cancel',
};

export const statusColors: Partial<Record<SupportedFilterStatus, ColorName>> = {
  requested: 'accent-blue',
  'in-progress': 'accent-green',
  failed: 'risk-high',
  completed: 'status-success',
  cancelled: 'accent-pink',
};

export const codeDisplay: Record<TaskCode, string> = {
  [TaskCode.ClinicalImpressionFollowUp]:
    strings.TaskCodeDisplayClinicalImpressionFollowUp,
  [TaskCode.ClinicalImpressionReview]:
    strings.TaskCodeDisplayClinicalImpressionReview,
  [TaskCode.ServiceRequestCreate]: strings.TaskCodeDisplayServiceRequestCreate,
  [TaskCode.DiagnosticReportFollowUp]:
    strings.TaskCodeDisplayDiagnosticReportFollowUp,
  [TaskCode.MedicationRequestCreate]:
    strings.TaskCodeDisplayMedicationRequestCreate,
  [TaskCode.MedicationRequestFollowUp]:
    strings.TaskCodeDisplayMedicationRequestFollowUp,
  [TaskCode.MediaFollowUp]: strings.TaskCodeDisplayMediaFollowUp,
  [TaskCode.OtherContact]: strings.TaskCodeDisplayOtherContact,
  [TaskCode.OtherSchedule]: strings.TaskCodeDisplayOtherSchedule,
  [TaskCode.OtherHomeVisit]: strings.TaskCodeDisplayHomeVisit,
  [TaskCode.OtherClinicalCallOut]: strings.TaskCodeDisplayClinicalCallOut,
};

export interface BaseTaskListProps extends useTasksProps {
  taskListSWRKey: string;
  navigate?: NavigateToTask;
  setOwner?: React.Dispatch<React.SetStateAction<OwnerFilter | undefined>>;
}

/**
 * Shared type for components that enable viewing and listing of tasks
 */
export type BaseTaskList = React.FC<BaseTaskListProps>;

export const taskTitle = (
  task: TaskGetResponse,
  showName: boolean,
): string | undefined => {
  const title = task.code && codeDisplay[task.code];

  if (!showName) {
    return title;
  }

  return compact([task.for?.display, title]).join(' - ');
};

export const taskDate = (task: TaskGetResponse) =>
  task.created ? shortMonthDateFormatter(new Date(task.created)) : undefined;

export const taskDisplay: Partial<Record<ResourceType, string>> = {
  Device: strings.TaskButtonViewDevice,
  Organization: strings.TaskButtonViewOrganization,
  ClinicalImpression: strings.TaskButtonViewClinicalImpression,
  Observation: strings.TaskButtonViewObservation,
  ServiceRequest: strings.TaskButtonViewServiceRequest,
  MedicationRequest: strings.TaskButtonViewMedicationRequest,
  Encounter: strings.TaskButtonViewEncounter,
  Coverage: strings.TaskButtonViewCoverage,
  Media: strings.TaskButtonViewMedia,
};
